import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Briefcase, Star } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { FavoriteButton } from './FavoriteButton';
import type { Candidate } from '../types';

interface CandidateCardProps {
  candidate: Candidate;
}

const seniorityLabels: Record<string, string> = {
  junior: 'Junior',
  mid: 'Confirmé',
  senior: 'Senior',
  expert: 'Expert',
  lead: 'Lead',
};

const availabilityLabels: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'secondary' }> = {
  immediate: { label: 'Immédiat', variant: 'success' },
  notice_1_month: { label: '1 mois', variant: 'warning' },
  notice_2_months: { label: '2 mois', variant: 'warning' },
  notice_3_months: { label: '3 mois', variant: 'secondary' },
  not_available: { label: 'Non disponible', variant: 'secondary' },
};

export function CandidateCard({ candidate }: CandidateCardProps) {
  const initials = `${candidate.firstName[0]}${candidate.lastName[0]}`.toUpperCase();
  const fullName = `${candidate.firstName} ${candidate.lastName}`;
  const availability = candidate.availability ? availabilityLabels[candidate.availability] : null;

  return (
    <Link to={`/itc/candidates/${candidate.id}`}>
      <Card className="relative hover:shadow-md transition-shadow cursor-pointer">
        <FavoriteButton
          candidateId={candidate.id}
          isFavorite={candidate.isFavorite}
          className="absolute right-2 top-2 z-10"
        />
        <CardHeader className="flex flex-row items-start gap-4 pb-2 pr-8">
          <Avatar className="h-12 w-12">
            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold truncate">{fullName}</h3>
              {candidate.rating != null && candidate.rating > 0 && (
                <div className="flex items-center gap-1 text-yellow-500">
                  <Star className="h-4 w-4 fill-current" />
                  <span className="text-sm">{candidate.rating}</span>
                </div>
              )}
            </div>
            {candidate.jobs && candidate.jobs.length > 0 ? (
              <div className="flex flex-wrap gap-1 mt-1">
                {candidate.jobs.slice(0, 3).map((job) => (
                  <Badge
                    key={job.jobId}
                    variant={job.isPrimary ? 'default' : 'secondary'}
                    className="text-xs py-0 px-1.5"
                  >
                    {job.jobName}
                  </Badge>
                ))}
                {candidate.jobs.length > 3 && (
                  <span className="text-xs text-muted-foreground self-center">
                    +{candidate.jobs.length - 3}
                  </span>
                )}
              </div>
            ) : candidate.currentTitle ? (
              <p className="text-sm text-muted-foreground truncate">{candidate.currentTitle}</p>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {candidate.currentCompany && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Briefcase className="h-4 w-4" />
              <span className="truncate">{candidate.currentCompany}</span>
            </div>
          )}

          {candidate.locationCity && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" />
              <span>{candidate.locationCity}</span>
            </div>
          )}

          {candidate.email && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Mail className="h-4 w-4" />
              <span className="truncate">{candidate.email}</span>
            </div>
          )}

          {candidate.phone && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Phone className="h-4 w-4" />
              <span>{candidate.phone}</span>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            {candidate.seniority && (
              <Badge variant="outline">{seniorityLabels[candidate.seniority] || candidate.seniority}</Badge>
            )}
            {availability && (
              <Badge variant={availability.variant}>{availability.label}</Badge>
            )}
            {candidate.yearsExperience != null && candidate.yearsExperience > 0 && (
              <Badge variant="secondary">{candidate.yearsExperience} ans exp.</Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
